import {
  runTransaction,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import type {
  ExpenseEntryData,
  ManualInvoiceAdjustment,
  MatterDraftInvoiceState,
  TimeEntryData,
} from '@shared/types';
import {
  attachMatterDraftInvoiceSourcesInTransaction,
  mutateMatterDraftInvoiceSourceInTransaction,
  updateMatterDraftInvoiceManualAdjustmentsInTransaction,
  type MatterDraftInvoiceAttachSourcesResult,
  type MatterDraftInvoiceManualAdjustmentsResult,
  type MatterDraftInvoiceSourceAction,
  type MatterDraftInvoiceSourceRef,
  type MatterDraftInvoiceSourceResult,
} from './matterDraftInvoiceStateMutations';
import {
  clearDeletedTimeEntryCommunicationLink,
  type SourceEntryCommunicationCleanupQuery,
} from './sourceEntryCommunicationCleanup';

export interface MatterDraftInvoiceStateActionQuery extends SourceEntryCommunicationCleanupQuery {
  matterDraftInvoiceState(
    matterId: string,
  ): DocumentReference<MatterDraftInvoiceState>;
  timeEntry(entryId: string): DocumentReference<TimeEntryData>;
  expenseEntry(entryId: string): DocumentReference<ExpenseEntryData>;
}

export interface MatterDraftInvoiceStateActionDb {
  firestore: Firestore;
  query: MatterDraftInvoiceStateActionQuery;
}

export interface MutateMatterDraftInvoiceSourceProps {
  matterId: string;
  entryType: 'timeEntry' | 'expenseEntry';
  entryId: string;
  action: MatterDraftInvoiceSourceAction;
}

export interface AttachMatterDraftInvoiceSourcesProps {
  matterId: string;
  entryType: 'timeEntry' | 'expenseEntry';
  entryIds: string[];
}

export interface UpdateMatterDraftInvoiceManualAdjustmentsProps {
  matterId: string;
  buildManualAdjustments: (
    adjustments: ManualInvoiceAdjustment[],
  ) => ManualInvoiceAdjustment[];
}

export const mutateMatterDraftInvoiceSource = async (
  db: MatterDraftInvoiceStateActionDb,
  props: MutateMatterDraftInvoiceSourceProps,
): Promise<MatterDraftInvoiceSourceResult> => {
  return await runTransaction(db.firestore, async (transaction) =>
    mutateMatterDraftInvoiceSourceInTransaction({
      transaction,
      matterId: props.matterId,
      draftStateRef: db.query.matterDraftInvoiceState(props.matterId),
      sourceRef: sourceRefFor(db, props.entryType, props.entryId),
      action: props.action,
      beforeDelete:
        props.action === 'delete' && props.entryType === 'timeEntry'
          ? async (source) =>
              await clearDeletedTimeEntryCommunicationLink({
                query: db.query,
                transaction,
                entryId: props.entryId,
                timeEntry: source as TimeEntryData,
              })
          : undefined,
    }),
  );
};

export const attachMatterDraftInvoiceSources = async (
  db: MatterDraftInvoiceStateActionDb,
  props: AttachMatterDraftInvoiceSourcesProps,
): Promise<MatterDraftInvoiceAttachSourcesResult> =>
  await runTransaction(db.firestore, async (transaction) =>
    attachMatterDraftInvoiceSourcesInTransaction({
      transaction,
      matterId: props.matterId,
      draftStateRef: db.query.matterDraftInvoiceState(props.matterId),
      sourceRefs: props.entryIds.map((entryId) =>
        sourceRefFor(db, props.entryType, entryId),
      ),
    }),
  );

export const updateMatterDraftInvoiceManualAdjustments = async (
  db: MatterDraftInvoiceStateActionDb,
  props: UpdateMatterDraftInvoiceManualAdjustmentsProps,
): Promise<MatterDraftInvoiceManualAdjustmentsResult> =>
  await runTransaction(db.firestore, async (transaction) =>
    updateMatterDraftInvoiceManualAdjustmentsInTransaction({
      transaction,
      matterId: props.matterId,
      draftStateRef: db.query.matterDraftInvoiceState(props.matterId),
      buildManualAdjustments: props.buildManualAdjustments,
    }),
  );

const sourceRefFor = (
  db: MatterDraftInvoiceStateActionDb,
  entryType: 'timeEntry' | 'expenseEntry',
  entryId: string,
): MatterDraftInvoiceSourceRef => ({
  entryType,
  entryId,
  ref:
    entryType === 'timeEntry'
      ? (db.query.timeEntry(entryId) as MatterDraftInvoiceSourceRef['ref'])
      : (db.query.expenseEntry(entryId) as MatterDraftInvoiceSourceRef['ref']),
});
